import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import WarrantyDataItemComponent from './WarrantyDataItemComponent';
import debounce from 'lodash.debounce';
import { loadWarrantyItems } from '../actionCreators';
import { loadPartnersFiltered } from 'actions/actionCreators/dictionariesActions';
import { getDocumentAssets } from '../selectors';
import { getCurrencies } from 'selectors/dictionariesSelectors';

const checkFilters = (filters, prevFilters) => {
  if (filters.page === prevFilters.page) filters.page = 1;
  return true;
};

export const WarrantyDataItemContainer = ({
  assets,
  warranty,
  warrantyTypes,
  partnerTypes,
  validationInfo,
  onPropertyChange,
  idx,
  actions,
  partnerRoles,
  warrantyItemVisible,
  editRight,
  guarantors,
  userIsDealer
}) => {
  const handleItemPropertyChange = useCallback(
    prop => e => {
      switch (prop) {
        case 'dimensionPeriodYear':
        case 'dimensionPeriodMonth': {
          onPropertyChange(idx, prop, e.target.checked);
          break;
        }
        case 'dictionaryWarrantyTypeId':
        case 'warrantyTypeId':
        case 'warrantyItem':
        case 'amount':
        case 'validFrom':
        case 'validTo':
        case 'documentDetail':
        case 'qtty':
        case 'guarantor':
        case 'issuerTypeId': {
          onPropertyChange(idx, prop, e);
          break;
        }
        case 'guaranteeNumber':
        case 'freeTxt': {
          onPropertyChange(idx, prop, e);
          break;
        }
        case 'issuer': {
          onPropertyChange(idx, prop, { issuerId: e.id, issuerName: e.value });
          break;
        }
        default:
          break;
      }
    },
    [idx, onPropertyChange]
  );

  const loadWarrantyItemsDebounce = debounce(actions.loadWarrantyItems, 500, { leading: true });
  const loadWarrantyItemsInternal = useCallback(
    input => {
      if (!input) {
        return Promise.resolve({ options: [] });
      }

      return loadWarrantyItemsDebounce(warranty.warrantyTypeId, input).then(data => {
        return {
          options: data.map(item => {
            return { itemId: item.itemId, itemName: item.itemName };
          })
        };
      });
    },
    [loadWarrantyItemsDebounce, warranty.warrantyTypeId]
  );

  return (
    <WarrantyDataItemComponent
      warranty={warranty}
      onPropertyChange={handleItemPropertyChange}
      loadWarrantyItems={loadWarrantyItemsInternal}
      validationInfo={validationInfo}
      assets={assets}
      guarantors={guarantors}
      partnerTypes={partnerTypes}
      checkFilters={checkFilters}
      loadPartners={actions.loadPartnersFiltered}
      idx={idx}
      warrantyTypes={warrantyTypes}
      partnerRoles={partnerRoles}
      warrantyItemVisible={warrantyItemVisible}
      editRight={editRight}
      userIsDealer={userIsDealer}
    />
  );
};

WarrantyDataItemContainer.propTypes = {
  warranty: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  validationInfo: PropTypes.object.isRequired,
  assets: PropTypes.array.isRequired,
  warrantyTypes: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  idx: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  partnerRoles: PropTypes.array.isRequired,
  warrantyItemVisible: PropTypes.bool,
  editRight: PropTypes.bool.isRequired,
  guarantors: PropTypes.array.isRequired,
  folderOffer: PropTypes.object,
  userIsDealer: PropTypes.bool
};

function mapStateToProps(state, ownProps) {
  const key = ownProps.folderOffer
    ? `${ownProps.folderOffer.offerId}-${ownProps.folderOffer.offerSiteId}`
    : `${state.currentOffer.header.documentId}-${state.currentOffer.header.siteId}`;

  return {
    userIsDealer: !state.userInfo.dealerIsSelfPartner && !state.userInfo.userIsAdmin,
    warrantyTypes: state.dictionaries.warrantyTypes.isLoaded ? state.dictionaries.warrantyTypes.items : [],
    guarantors: state.document.guarantors[key].loaded ? state.entities.guarantors[key] : [],
    currencies: getCurrencies(state),
    partnerTypes: state.clientData.partnerTypes,
    assets: getDocumentAssets(state.currentOffer.assetData.assetsHeader),
    partnerRoles: state.dictionaries.partnerRoles.isLoaded ? state.dictionaries.partnerRoles.items : []
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPartnersFiltered,
        loadWarrantyItems
      },
      dispatch
    )
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(WarrantyDataItemContainer);
